import useSWR from 'swr';
import { fetcher } from '../../services/fetcher';
import { moduleConfig } from './config';

export interface Client {
  clientId: number;
  clientName?: string;
  clientName2?: string;
  clientCode?: string;
  contactPerson?: string;
  clientAddress?: string;
  clientCity?: string;
  clientZipCode?: string;
  clientState?: string;
  clientCountryId?: number;
  clientCountryDescr?: {
    pc_descr?: string;
  };
  clientWebSite?: string;
  clientPhone?: string;
  clientPhone2?: string;
  clientEmail?: string;
  clientEmail2?: string;
  clientNotes?: string;
  clientVATNumber?: string;
  clientPaymentTerms?: number;
  clientTaxAuthority?: number;
  isClient: boolean;
  clientIsActive: boolean;
  gemiNumber?: string;
  dateStarted?: string;
  sourceUrl?: string;
  clientUserName?: string | null;
  clientUserPassword?: string | null;
  clientCalls?: Array<{
    clientCallDate?: string | null;
  }>;
  _count?: {
    clientCalls?: number;
    clientReminders?: number;
    clientAppointments?: number;
    clientTasks?: number;
    clientServices?: number;
    FileAttachment?: number;
  };
  clientCreatedAt: string;
  clientUpdatedAt?: string;
  FileAttachment?: Array<{
    id: number;
    fileName?: string;
    filePath?: string;
    fileType?: string;
    fileSize?: number;
    uploadedAt?: string;
  }>;
}

export interface ClientsResponse {
  data: Client[];
  total: number;
  page: number;
  limit: number;
}

// Normalize list response (backend may return array or { data: [] } / { records: [] })
function toClientsList(raw: unknown): Client[] {
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === 'object' && 'data' in raw && Array.isArray((raw as ClientsResponse).data)) {
    return (raw as ClientsResponse).data;
  }
  if (raw && typeof raw === 'object' && 'records' in raw && Array.isArray((raw as { records: Client[] }).records)) {
    return (raw as { records: Client[] }).records;
  }
  return [];
}

export const useClients = (searchParams?: string) => {
  const queryString = searchParams ? `?searchParams=${encodeURIComponent(searchParams)}` : '';
  const { data, error, isLoading, mutate } = useSWR<Client[] | ClientsResponse>(
    `${moduleConfig.module.api.list}${queryString}`,
    fetcher,
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
    }
  );

  const clients = toClientsList(data);
  return {
    data: clients,
    clients,
    total: clients.length,
    isLoading,
    isError: error,
    mutate,
  };
};

export const useClient = (id: number) => {
  const { data, error, isLoading, mutate } = useSWR<Client>(
    id ? moduleConfig.module.api.detail(id) : null,
    fetcher,
    {
      revalidateOnFocus: true,
    }
  );

  return {
    data: data,
    client: data,
    isLoading,
    isError: error,
    mutate,
  };
};

/** Summary response from GET /crm-clients/:id/summary (client + _count for all relations). */
export interface ClientSummaryCounts {
  clientCalls?: number;
  clientAppointments?: number;
  clientReminders?: number;
  clientInvoices?: number;
  clientServices?: number;
  clientTasks?: number;
  clientGeneralNotes?: number;
  clientTodos?: number;
  FileAttachment?: number;
  supportTickets?: number;
  crm_ClientsContacts?: number;
  crm_packages?: number;
  supportAgreements?: number;
  linkedEmails?: number;
  projects?: number;
  crm_Leads?: number;
  vaultFolders?: number;
  offers?: number;
}

/** KPIs returned by GET /crm-clients/:id/summary (kpis key). */
export interface ClientSummaryKpis {
  totalInvoiced: number;
  totalPaid: number;
  unpaidAmount: number;
  overdueInvoicesCount: number;
  nextAppointmentDate: string | null;
  servicesExpiringCount: number;
}

export interface ClientSummary extends Omit<Client, '_count'> {
  _count?: ClientSummaryCounts;
  /** From summary endpoint */
  clientTaxAuthorityDescr?: { pt_descr?: string } | null;
  /** Aggregated KPIs for summary modal */
  kpis?: ClientSummaryKpis;
}

const CLIENT_SUMMARY_CACHE_MS = 60_000; // 1 min – avoid refetching too often when reopening modal

export const useClientSummary = (clientId: number | null) => {
  const url = clientId ? `${moduleConfig.module.api.detail(clientId)}/summary` : null;
  const { data, error, isLoading, mutate } = useSWR<ClientSummary | null>(
    url,
    fetcher,
    {
      revalidateOnFocus: true,
      dedupingInterval: CLIENT_SUMMARY_CACHE_MS,
      revalidateIfStale: true,
    }
  );
  return { data, clientSummary: data, isLoading, isError: error, mutate };
};

